import { formatFullName } from '@dokucoach/common-types';
import { Box } from '@material-ui/core';
import { EmptyStateWithText } from 'components/EmptyState';
import Search from 'components/Layout/components/Search';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResidentGroupPropType, ResidentPropType } from 'services/residentGroups';
import { AvatarImageType } from 'utils/getAvatarImage';
import { byLastName } from 'utils/sortBy';
import { useContextLayout } from '../../context';
import GroupList from './components/GroupList';
import ResidentList from './components/ResidentList';
import AsideMenuListContext from './context';

interface Props {
  tabPaneRef: React.Ref<HTMLElement>;
  onEditGroup: (id: string) => void;
}

const AsideMenuList = ({ tabPaneRef, onEditGroup }: Props) => {
  const { residentGroups } = useContextLayout();
  const { t } = useTranslation();
  const [isSearching, setIsSearching] = useState(false);
  const [allResidentsGroup, setAllResidentsGroup] = useState<ResidentGroupPropType>();
  const [filteredResidents, setFilteredResidents] = useState<ResidentPropType[]>([]);

  useEffect(() => {
    if (residentGroups?.length) {
      const groupOfAll = residentGroups.find((g) => g.isDefault && g.name === 'Alle Bewohner');
      if (!groupOfAll) {
        throw new Error('Could not find the group "All residents"');
      }
      setAllResidentsGroup(groupOfAll);
    }
  }, [residentGroups]);

  const onSearch = (term?: string) => {
    if (!term || !allResidentsGroup) {
      setIsSearching(false);
      setFilteredResidents([]);
    } else {
      const search = term.toLowerCase();
      setFilteredResidents(
        allResidentsGroup.residents.filter(
          (resident) =>
            resident.name.last.toLowerCase().includes(search) ||
            resident.name.firstFull.toLowerCase().includes(search),
        ),
      );
      setIsSearching(true);
    }
  };

  return residentGroups ? (
    <AsideMenuListContext>
      <div className={`tab-pane px-lg-3 fade show active`}>
        <Search onSearch={onSearch} showCancel={true} />
      </div>
      <ul className="menu-nav" style={isSearching ? { paddingRight: '8px' } : undefined}>
        {isSearching ? (
          filteredResidents.length ? (
            <>
              {filteredResidents.map((resident: ResidentPropType) => (
                <ResidentList
                  key={resident.id}
                  id={resident.id}
                  label={formatFullName(resident.name)}
                  description={resident?.bed?.label ?? t('no_bed_set')}
                  gender={resident.name.gender}
                  warning={1}
                  parentIndex={0}
                  isAbsent={resident.isAbsent}
                  tabPaneRef={tabPaneRef}
                  allResidentsGroupId={allResidentsGroup?.id}
                  avatarImage={
                    (resident.profilePictureUrl && { url: resident.profilePictureUrl }) || {
                      svg: AvatarImageType.OLD,
                    }
                  }
                />
              ))}
            </>
          ) : (
            <Box sx={{ pl: 2 }}>
              <EmptyStateWithText text={t('no_results_for_current_settings')} verticalPositionTop />
            </Box>
          )
        ) : (
          <>
            {residentGroups.map((group: ResidentGroupPropType, mainIndex: number) => (
              <GroupList
                key={group.id}
                label={group.name}
                warning={9}
                residents={group.residents.length}
                id={group.id}
                hideEdit={group.hideEdit || group.isDefault || false}
                onEdit={() => onEditGroup(group.id)}
              >
                {group.residents.sort(byLastName).map((resident: ResidentPropType) => (
                  <ResidentList
                    key={resident.id}
                    id={resident.id}
                    label={formatFullName(resident.name)}
                    description={resident?.bed?.label ?? t('no_bed_set')}
                    gender={resident.name.gender}
                    isAbsent={resident.isAbsent}
                    warning={1}
                    parentIndex={mainIndex}
                    tabPaneRef={tabPaneRef}
                    avatarImage={
                      (resident.profilePictureUrl && { url: resident.profilePictureUrl }) || {
                        svg: AvatarImageType.OLD,
                      }
                    }
                  />
                ))}
              </GroupList>
            ))}
          </>
        )}
      </ul>
    </AsideMenuListContext>
  ) : null;
};

export default AsideMenuList;
